/**
 * The export form: which business, and which period. The period reuses the reports' presets, so
 * "last year" means the same dates in both places. Last year is the default, because a finished
 * year is what an accountant is usually handed.
 */

import { z } from "zod";
import type { components } from "@/api/schema";
import { PERIODS, periodRange } from "./filters";

export type ExportBody = components["schemas"]["ExportIn"];

export const exportSchema = z
  .object({
    business_id: z.string().min(1, "reports.export.errors.businessRequired"),
    period: z.enum(PERIODS),
    start: z.string(),
    end: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.period !== "custom") return;
    if (!values.start) {
      ctx.addIssue({
        code: "custom",
        path: ["start"],
        message: "reports.export.errors.dateRequired",
      });
    }
    if (!values.end) {
      ctx.addIssue({
        code: "custom",
        path: ["end"],
        message: "reports.export.errors.dateRequired",
      });
    }
    if (values.start && values.end && values.start > values.end) {
      ctx.addIssue({ code: "custom", path: ["end"], message: "reports.export.errors.backwards" });
    }
  });

export type ExportValues = z.infer<typeof exportSchema>;

export const emptyExport: ExportValues = {
  business_id: "",
  period: "last-year",
  start: "",
  end: "",
};

/** The request: a preset becomes its dates; "all time" sends none. */
export function toExportBody(values: ExportValues, today: string): ExportBody {
  const { start, end } =
    values.period === "custom"
      ? { start: values.start, end: values.end }
      : periodRange(values.period, today);
  return {
    kind: "csv",
    business_id: values.business_id,
    ...(start ? { start } : {}),
    ...(end ? { end } : {}),
  };
}
